import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  FailureNotice,
  type HeatStrings,
  heatMessages,
} from "@/components/tools/chem-physical/heat-shared";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  deltaIn,
  ENERGY_UNITS,
  HEAT_MASS_UNITS,
  HEAT_TERMS,
  type HeatSolution,
  type HeatTerm,
  nearestSubstance,
  SPECIFIC_HEAT_UNITS,
  solveHeat,
  tableValueIn,
  unitFactor,
  unitOptionsOf,
  unitSymbol,
} from "@/lib/tool-kit/chem-physical/heat";
import { SPECIFIC_HEATS } from "@/lib/tool-kit/chem-physical/heat-data";
import {
  TEMPERATURE_UNITS,
  temperatureSymbol,
} from "@/lib/tool-kit/chem-physical/units";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
} from "@/lib/tool-kit/chemistry/format";

const CUSTOM = "custom";
const DEFAULT_SIG = 4;
const TEMPERATURE_OPTIONS = TEMPERATURE_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));

const START_TEXT: Record<HeatTerm, string> = {
  q: "",
  m: "250.0",
  c: "",
  ti: "20.0",
  tf: "80.0",
};

function HeatAnswer({
  solution,
  texts,
  units,
  sig,
  strings,
}: {
  solution: HeatSolution;
  texts: Record<HeatTerm, string>;
  units: { q: string; m: string; c: string; t: string };
  sig: number;
  strings: HeatStrings;
}) {
  const fmt = useToolNumber();
  const { unknown, values } = solution;
  const labels: Record<HeatTerm, string> = {
    q: strings.termQ,
    m: strings.termM,
    c: strings.termC,
    ti: strings.termTi,
    tf: strings.termTf,
  };
  const answerSymbol = {
    q: unitSymbol(ENERGY_UNITS, units.q),
    m: unitSymbol(HEAT_MASS_UNITS, units.m),
    c: unitSymbol(SPECIFIC_HEAT_UNITS, units.c),
    ti: temperatureSymbol(units.t),
    tf: temperatureSymbol(units.t),
  }[unknown];
  const answerText = `${formatSig(fmt, solution.answer, sig)} ${answerSymbol}`;
  const p = (value: number) => formatPlain(fmt, value);
  const base = {
    q: `${p(values.q)} J`,
    m: `${p(values.m)} g`,
    c: `${p(values.c)} ${strings.baseSpecificHeat}`,
    ti: `${p(values.ti)} K`,
    tf: `${p(values.tf)} K`,
    delta: `${p(solution.delta)} K`,
  };
  const result = (unit: string, digits = sig + 1) => {
    const value = values[unknown];
    return `${formatSig(fmt, value, digits)} ${unit}`;
  };
  const conversions = (["q", "m", "c"] as const)
    .filter((term) => term !== unknown)
    .filter((term) => {
      const list = {
        q: ENERGY_UNITS,
        m: HEAT_MASS_UNITS,
        c: SPECIFIC_HEAT_UNITS,
      }[term];
      return unitFactor(list, units[term]) !== 1;
    })
    .map((term) =>
      t(strings.convertLine, {
        label: labels[term],
        value: texts[term].trim(),
        unit: unitSymbol(
          { q: ENERGY_UNITS, m: HEAT_MASS_UNITS, c: SPECIFIC_HEAT_UNITS }[term],
          units[term]
        ),
        converted: base[term],
      })
    );
  if (units.t !== "K") {
    for (const term of ["ti", "tf"] as const) {
      if (term !== unknown) {
        conversions.push(
          t(strings.convertLine, {
            label: labels[term],
            value: texts[term].trim(),
            unit: temperatureSymbol(units.t),
            converted: base[term],
          })
        );
      }
    }
  }
  const rearranged = {
    q: strings.rearrangedQ,
    m: strings.rearrangedM,
    c: strings.rearrangedC,
    ti: strings.rearrangedTi,
    tf: strings.rearrangedTf,
  }[unknown];
  const substitutedKey = {
    q: strings.substitutedQ,
    m: strings.substitutedM,
    c: strings.substitutedC,
    ti: strings.substitutedTi,
    tf: strings.substitutedTf,
  }[unknown];
  const resultUnit = {
    q: "J",
    m: "g",
    c: strings.baseSpecificHeat,
    ti: "K",
    tf: "K",
  }[unknown];
  const deltaLine =
    unknown === "ti" || unknown === "tf"
      ? t(strings.deltaFromQ, {
          q: base.q,
          m: base.m,
          c: base.c,
          delta: base.delta,
        })
      : t(strings.deltaLine, { ti: base.ti, tf: base.tf, delta: base.delta });
  const absorbed = values.q > 0;
  const needsConversion =
    (unknown === "ti" || unknown === "tf") && units.t !== "K";
  const shownDelta = `${formatSig(fmt, deltaIn(solution.delta, units.t), sig)} ${temperatureSymbol(units.t)}`;
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={labels[unknown]} value={answerText} />
        <StatCard label={strings.deltaT} value={shownDelta} />
        <StatCard
          label={strings.direction}
          value={absorbed ? strings.absorbed : strings.released}
        />
      </StatGrid>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.equationHeat}</p>
        <p>{rearranged}</p>
        {conversions.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>{deltaLine}</p>
        <p>
          {t(substitutedKey, {
            q: base.q,
            m: base.m,
            c: base.c,
            ti: base.ti,
            tf: base.tf,
            delta: base.delta,
            result: result(resultUnit),
          })}
        </p>
        {needsConversion ? (
          <p>
            {t(strings.answerLine, { result: result("K"), answer: answerText })}
          </p>
        ) : null}
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          label: labels[unknown],
          value: answerText,
        })}
      />
    </>
  );
}

export function HeatPanel({
  strings,
  sigChoice,
}: {
  strings: HeatStrings;
  sigChoice: string;
}) {
  const [unknown, setUnknown] = useState<HeatTerm>("q");
  const [text, setText] = useState(START_TEXT);
  const [substance, setSubstance] = useState("water");
  const [unit, setUnit] = useState({ q: "kJ", m: "g", c: "JgC", t: "C" });
  const fmt = useToolNumber();

  const tableValue =
    substance === CUSTOM ? null : tableValueIn(substance, unit.c);
  const cText = tableValue === null ? text.c : formatPlain(fmt, tableValue);
  const texts = { ...text, c: cText };
  const outcome = solveHeat({ unknown, text: texts, unit });
  const typed = HEAT_TERMS.filter((term) => term !== unknown).map(
    (term) => texts[term]
  );
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);

  const labels: Record<HeatTerm, string> = {
    q: strings.termQ,
    m: strings.termM,
    c: strings.termC,
    ti: strings.termTi,
    tf: strings.termTf,
  };
  const unitLabels = {
    q: strings.unitEnergy,
    m: strings.unitMass,
    c: strings.unitSpecificHeat,
    t: strings.unitTemperature,
  };
  const unitList = {
    q: unitOptionsOf(ENERGY_UNITS),
    m: unitOptionsOf(HEAT_MASS_UNITS),
    c: unitOptionsOf(SPECIFIC_HEAT_UNITS),
    t: TEMPERATURE_OPTIONS,
  };
  const field = (term: HeatTerm) => {
    const group = term === "ti" || term === "tf" ? "t" : term;
    return (
      <QuantityInput
        id={`sh-${term}`}
        key={term}
        label={
          unknown === term
            ? t(strings.answerSuffix, { label: labels[term] })
            : labels[term]
        }
        onUnitChange={(value) => setUnit({ ...unit, [group]: value })}
        onValueChange={(value) => {
          if (term === "c") {
            setSubstance(CUSTOM);
          }
          setText({ ...text, [term]: value });
        }}
        solved={unknown === term}
        unit={unit[group]}
        unitLabel={unitLabels[group]}
        units={unitList[group]}
        value={texts[term]}
      />
    );
  };
  const substances = strings.substances;
  const match =
    unknown === "c" && outcome.ok ? nearestSubstance(outcome.values.c) : null;
  return (
    <>
      <PillGroup
        label={strings.solveLabel}
        name="sh-solve"
        onChange={setUnknown}
        options={HEAT_TERMS.map((term) => ({
          value: term,
          label: labels[term],
        }))}
        value={unknown}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          hint={strings.substanceHint}
          id="sh-substance"
          label={strings.substanceLabel}
          onChange={setSubstance}
          options={[
            { value: CUSTOM, label: strings.substanceCustom },
            ...SPECIFIC_HEATS.map((entry) => ({
              value: entry.id,
              label: substances[entry.id as keyof typeof substances],
            })),
          ]}
          value={substance}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">{HEAT_TERMS.map(field)}</div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <HeatAnswer
            sig={sig}
            solution={outcome}
            strings={strings}
            texts={texts}
            units={unit}
          />
        ) : (
          <FailureNotice
            failure={outcome}
            labels={labels}
            messages={heatMessages(strings)}
          />
        )}
        {match ? (
          <Notice>
            {t(strings.matchNote, {
              name: strings.substances[
                match.id as keyof typeof strings.substances
              ],
              value: formatPlain(fmt, match.value),
            })}
          </Notice>
        ) : null}
      </section>
    </>
  );
}
